import { NavLink, Link, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import { MoreHorizontal, type LucideIcon } from 'lucide-react'
import { clsx } from 'clsx'
import { useSidebar } from './SidebarContext'

export interface SidebarItem { to: string; label: string; icon: LucideIcon }
export interface SidebarSection { title: string; items: SidebarItem[] }

// TailAdmin AppSidebar: fixed white column, 290px expanded / 90px collapsed (hover re-expands),
// off-canvas below xl. Group titles are uppercase gray-400; items use the menu-item recipe
// (brand-50/brand-500 when active, gray-700 + gray-100 hover otherwise).
export function AppSidebar({ sections, brand, homeTo, emptyMessage }: {
  sections: SidebarSection[]
  brand: { name: string; icon: LucideIcon | string }
  homeTo: string
  emptyMessage?: string
}) {
  const { isExpanded, isHovered, isMobileOpen, setIsHovered, closeMobile } = useSidebar()
  const location = useLocation()
  const wide = isExpanded || isHovered || isMobileOpen

  // Navigating from the mobile drawer should close it, like TailAdmin's own behavior.
  useEffect(() => { closeMobile() }, [location.pathname, closeMobile])

  return (
    <>
      <aside
        className={clsx(
          'fixed left-0 top-0 z-[9999] flex h-screen flex-col border-r border-gray-200 bg-white px-5 text-gray-900 transition-all duration-300 ease-in-out xl:translate-x-0',
          wide ? 'w-[290px]' : 'w-[90px]',
          isMobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        onMouseEnter={() => !isExpanded && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className={clsx('flex py-8', wide ? 'justify-start' : 'xl:justify-center')}>
          <Link to={homeTo} className="flex items-center gap-3">
            <BrandMark icon={brand.icon} />
            {wide && <span className="text-xl font-semibold text-gray-900 whitespace-nowrap">{brand.name}</span>}
          </Link>
        </div>

        <div className="no-scrollbar flex flex-col overflow-y-auto duration-300 ease-linear">
          <nav className="mb-6">
            <div className="flex flex-col gap-4">
              {sections.length === 0 && emptyMessage && wide && (
                <p className="text-theme-sm text-gray-500">{emptyMessage}</p>
              )}
              {sections.map(section => (
                <div key={section.title}>
                  <h2 className={clsx('mb-4 flex text-xs uppercase leading-5 text-gray-400', wide ? 'justify-start' : 'xl:justify-center')}>
                    {wide ? section.title : <MoreHorizontal size={24} />}
                  </h2>
                  <ul className="flex flex-col gap-1">
                    {section.items.map(item => (
                      <li key={item.to}>
                        <NavLink
                          to={item.to}
                          title={wide ? undefined : item.label}
                          className={({ isActive }) => clsx(
                            'menu-item group',
                            isActive ? 'menu-item-active' : 'menu-item-inactive',
                            !wide && 'xl:justify-center',
                          )}
                        >
                          {({ isActive }) => (
                            <>
                              <item.icon size={24} strokeWidth={1.5} className={clsx('shrink-0', isActive ? 'menu-item-icon-active' : 'menu-item-icon-inactive')} />
                              {wide && <span className="truncate">{item.label}</span>}
                            </>
                          )}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </nav>
        </div>
      </aside>

      {/* Mobile backdrop — TailAdmin's Backdrop.tsx */}
      {isMobileOpen && (
        <div className="fixed inset-0 z-[9998] bg-gray-900/50 xl:hidden" onClick={closeMobile} />
      )}
    </>
  )
}

// Square brand mark used in place of TailAdmin's logo image: brand-500 tile with a white glyph
// (either a lucide icon or a letter).
export function BrandMark({ icon: Icon }: { icon: LucideIcon | string }) {
  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-500 text-white">
      {typeof Icon === 'string'
        ? <span className="text-base font-semibold leading-none">{Icon}</span>
        : <Icon size={18} strokeWidth={2} />}
    </span>
  )
}
